import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AbsenceContactPanel } from "../components/absences/AbsenceContactPanel.js";
import {
  EditableHtml,
  type EditableHtmlHandle,
} from "../components/statusReports/EditableHtml.js";
import { sanitizeReportHtml } from "../components/statusReports/sanitize.js";
import { useAppVersion, useCurrentUser } from "../hooks/useAuth.js";
import { describeApiError } from "../lib/errorText.js";

import {
  REPORT_LOCALE,
  ReportView,
  berichtT,
  buildOutlookWebHref,
  formatEur,
  formatMonth,
  htmlToPlainText,
  renderReportText,
  toOutlookHtml,
} from "@msc/report";
import type {
  StatusReportConfig,
  StatusReportConfigInput,
  StatusReportHighlight,
  StatusReportTicket,
} from "@msc/shared";

import {
  useAddHighlight,
  useAddOverride,
  useMarkReportSent,
  useRemoveHighlight,
  useRemoveOverride,
  useSetBudgetOrderNumber,
  useSetTicketHidden,
  useSetTicketNote,
  useGenerateAiSummaries,
  useReportableEngagements,
  useResetEditedReport,
  useRevertReportSent,
  useSaveEditedReport,
  useStatusReport,
  useUpdateReportConfig,
} from "../hooks/useStatusReports.js";

/**
 * Statusbericht — Ablösung der eigenständigen App `ServiceStatusberichte`.
 *
 * Zeigt je (Beauftragung × Monat) Budget, Faktura-Stunden, Ansprechpartner mit
 * Abwesenheiten und Ticketlisten. Alle Zahlen stammen aus den
 * Salesforce-Spiegeltabellen des CRM — die SharePoint-Excel der Altanwendung
 * entfällt (Spec: docs/mindCoder/specs/2026-08-02-statusbericht-integration-design.md).
 */

/** Die letzten zwölf abgeschlossenen Monate, neuester zuerst. */
function recentMonths(count = 12): string[] {
  const now = new Date();
  const cursor = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const months: string[] = [];
  for (let i = 1; i <= count; i += 1) {
    const d = new Date(cursor);
    d.setUTCMonth(d.getUTCMonth() - i);
    months.push(d.toISOString().slice(0, 7));
  }
  return months;
}

/**
 * Ein fehlgeschlagener Schreibvorgang, gemeldet **am Ort der Aktion**
 * (Befund B7).
 *
 * Zuvor stand genau ein Banner ganz oben auf der Seite — gemessen 1469 Pixel
 * ueber dem Speichern-Knopf, an dem der Anwender gerade stand. Wer unten
 * speicherte, sah schlicht nichts passieren. Und es blieb danach stehen.
 *
 * Deshalb dieses Bauteil statt eines Seitenbanners: Es steht bei den Knoepfen,
 * die es betrifft, nennt die Servermeldung samt Code (`describeApiError` sorgt
 * dafuer, dass nie nur das Wort „Fehler" uebrig bleibt) und laesst sich
 * schliessen. Der naechste Versuch raeumt es ohnehin ab — react-query setzt
 * den Fehlerzustand bei `mutate` zurueck.
 */
interface ResettableMutation {
  isError: boolean;
  error: unknown;
  reset: () => void;
}

function MutationAlert({ mutations }: { mutations: ResettableMutation[] }) {
  const { t } = useTranslation();
  const failed = mutations.find((m) => m.isError);
  if (!failed) return null;
  return (
    <Alert
      color="red"
      variant="light"
      title={t("common.error")}
      withCloseButton
      closeButtonLabel={t("common.close")}
      onClose={() => failed.reset()}
      mt="sm"
      data-testid="mutation-error"
    >
      {describeApiError(failed.error, t)}
    </Alert>
  );
}

/**
 * Seitenkopf der Statusberichte — Titel links, laufende Version rechts.
 *
 * **Warum hier und nicht global.** Gefragt wird nach der Version, wenn man
 * gerade an den Berichten etwas geändert hat und nachsehen will, ob der neue
 * Stand schon da ist. Genau auf dieser Seite steht man dann.
 *
 * **Was die Zahl ist — und was nicht.** Es gibt keine eigene Zählung für die
 * Statusberichte; sie ist die Version der ganzen Anwendung. Auf Staging der
 * kurze Commit-SHA, aus dem die Images gebaut wurden (`build.yml`), auf
 * Produktion der Release-Tag, lokal `dev`. Der SHA lässt sich unmittelbar
 * gegen den erwarteten Commit halten. Eine eigene Zählung nur für die
 * Berichte wäre eine zweite Wahrheit, die irgendwann von der ersten abweicht.
 *
 * Sie steht bewusst NICHT im Bericht selbst: Der geht an Kunden, und eine
 * Buildnummer hat dort nichts zu suchen.
 */
function Seitenkopf() {
  const { t } = useTranslation();
  const version = useAppVersion();
  return (
    <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
      <Title order={2}>{t("pages.statusReports.title")}</Title>
      {version ? (
        <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
          {t("app.version", { version })}
        </Text>
      ) : null}
    </Group>
  );
}

export function StatusReportsPage() {
  const { t, i18n } = useTranslation();
  /** Oberflächensprache — Auswahllisten, Knöpfe, Hinweise an den Bearbeiter. */
  const locale = i18n.language.startsWith("en") ? "en-GB" : "de-DE";
  /*
   * Fester Sprachkanal für den kundengerichteten Teil (Fachkonzept §13.1):
   * Berichtstext, Anrede, Einleitung und Grußformel der Kundenmail hängen am
   * Kunden, nicht an der Oberflächensprache des Absenders. Die Texte kommen
   * deshalb aus `berichtT` des Berichtspakets (`@msc/report`), nicht aus der
   * App-Instanz — derselbe Text wie auf dem Server.
   */

  // Wer den Entwurf unterschreibt: der Angemeldete. Faellt er aus, springt
  // `buildMailBody` auf den Service Lead des Berichts.
  const { user: angemeldet } = useCurrentUser();
  const angemeldeterName = angemeldet?.fullName ?? null;

  const {
    data: engagements,
    isLoading: loadingEngagements,
    isError: engagementsFailed,
    error: engagementsError,
  } = useReportableEngagements();
  const months = useMemo(() => recentMonths(), []);

  const [engagementId, setEngagementId] = useState<string | null>(null);
  const [month, setMonth] = useState<string>(months[0]!);

  // Vorbelegung: erste Beauftragung — die eigenen stehen oben (Sortierung in
  // listReportableEngagements).
  useEffect(() => {
    if (engagementId || !engagements || engagements.length === 0) return;
    setEngagementId(engagements[0]!.engagementId);
  }, [engagements, engagementId]);

  const {
    data: report,
    isLoading: loadingReport,
    isError: reportFailed,
    error: reportError,
  } = useStatusReport(engagementId, month);

  const updateConfig = useUpdateReportConfig(engagementId);
  const addHighlight = useAddHighlight(engagementId, month);
  const removeHighlight = useRemoveHighlight(engagementId, month);
  const addOverride = useAddOverride(engagementId, month);
  const removeOverride = useRemoveOverride(engagementId, month);
  const setTicketNote = useSetTicketNote(engagementId, month);
  const setTicketHidden = useSetTicketHidden(engagementId);
  const setOrderNumber = useSetBudgetOrderNumber(engagementId);
  const markSent = useMarkReportSent(engagementId, month);
  const revertSent = useRevertReportSent(engagementId, month);
  const [copied, setCopied] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(false);
  /** Rueckfrage vor „Als versendet markieren" (Befund B8). */
  const [confirmSend, setConfirmSend] = useState(false);
  /** Rueckfrage vor „Neu erzeugen" (Befund U8). */
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  /** „Mail entwerfen" wurde geklickt — der zweite Schritt steht an (U3). */
  const [mailPrepared, setMailPrepared] = useState(false);
  // Zeigt auf den INHALT des Berichts, nicht auf den Rahmen — sonst waechst
  // pro Bearbeitungszyklus eine Verschachtelung (Befund G10).
  const reportRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<EditableHtmlHandle>(null);
  /** Stand beim Betreten des Bearbeitungsmodus, einmalig eingefroren. */
  const [editSnapshot, setEditSnapshot] = useState<string | null>(null);
  /**
   * (Beauftragung × Monat), zu der der eingefrorene Stand gehoert (Befund B1).
   *
   * Ohne diese Herkunftsangabe laesst sich nicht feststellen, ob der Editor
   * noch zum angezeigten Bericht passt — und genau das war der Fehler.
   */
  const [editContext, setEditContext] = useState<{
    engagementId: string;
    month: string;
  } | null>(null);
  /** Gewuenschter Wechsel, der auf die Rueckfrage wartet (Befund B1). */
  const [pendingSwitch, setPendingSwitch] = useState<{
    engagementId?: string;
    month?: string;
  } | null>(null);
  const saveEdited = useSaveEditedReport(engagementId, month);
  const resetEdited = useResetEditedReport(engagementId, month);
  const aiSummaries = useGenerateAiSummaries(engagementId, month);

  /**
   * Gesaeubertes Markup der bearbeiteten Fassung.
   *
   * `useMemo`, weil der Aufruf zuvor bei JEDEM Neuaufbau ueber bis zu 500 kB
   * Markup lief (Befund W9). Neu gerechnet wird nur, wenn sich das
   * gespeicherte Markup aendert.
   */
  const sanitizedEditedHtml = useMemo(
    () => (report?.editedHtml ? sanitizeReportHtml(report.editedHtml) : null),
    [report?.editedHtml],
  );

  /**
   * Klartext des Berichts fuer Zwischenablage und Mail-Entwurf.
   *
   * Liegt eine von Hand bearbeitete Fassung vor, gewinnt SIE (Befund G10):
   * Zuvor entstand der Entwurf immer aus den Rohdaten, die Bearbeitung ging
   * auf dem Weg zum Kunden also verloren.
   */
  const reportPlainText = useMemo(
    () =>
      report
        ? sanitizedEditedHtml !== null
          ? htmlToPlainText(sanitizedEditedHtml)
          : renderReportText(report, REPORT_LOCALE, berichtT)
        : "",
    [report, sanitizedEditedHtml],
  );

  /** „Bearbeiten": den aktuellen Stand einfrieren und den Editor einhaengen. */
  const handleStartEdit = () => {
    if (!engagementId) return;
    setEditSnapshot(reportRef.current?.innerHTML ?? "");
    setEditContext({ engagementId, month });
    setEditing(true);
  };

  /** Bearbeitungsmodus verlassen und den lokalen Stand restlos wegwerfen. */
  const exitEdit = () => {
    setEditing(false);
    setEditSnapshot(null);
    setEditContext(null);
  };

  const handleCancelEdit = exitEdit;

  /**
   * Notausgang gegen die Kundenvermischung (Befund B1).
   *
   * Der Bearbeiten-Modus friert das gerenderte Markup in `editSnapshot` ein.
   * Wechselte die Beauftragung, blieb dieser Zustand stehen: Kopfzeile,
   * Auswahlfeld und Einstellungsblock zeigten den neuen Kunden, der
   * Berichtskoerper weiter den alten — samt dessen Budgetzahlen. „Bericht
   * kopieren", „Speichern" und „Als versendet markieren" waren dabei alle
   * aktiv; ein Klick haette fremde Zahlen an den Kunden geschickt oder
   * dauerhaft an der falschen Beauftragung abgelegt.
   *
   * Der uebliche Weg fragt vorher nach (s. `requestSwitch`). Diese Wirkung
   * haengt aber NICHT an einem bestimmten Bedienweg: Sobald der eingefrorene
   * Stand nicht mehr zum angezeigten Bericht gehoert, wird er verworfen —
   * egal, wodurch der Wechsel entstanden ist. Der vermischte Zustand kann
   * damit gar nicht erst entstehen.
   */
  const editContextMatches =
    editContext !== null &&
    editContext.engagementId === engagementId &&
    editContext.month === month;

  /**
   * Der Bearbeiten-Modus gilt NUR fuer den Bericht, aus dem er entstanden ist.
   *
   * Bewusst im Render abgeleitet und nicht erst im Effekt unten: Ein Effekt
   * laeuft nach dem Zeichnen, es bliebe also ein Bild lang der vermischte
   * Zustand sichtbar — und in genau diesem Bild waeren „Speichern" und
   * „Bericht kopieren" bedienbar. So kann er nicht einmal fuer ein Bild
   * entstehen; der Effekt raeumt danach den Zustand hinterher.
   */
  const isEditing = editing && editContextMatches;

  useEffect(() => {
    if (!editing || editContextMatches) return;
    setEditing(false);
    setEditSnapshot(null);
    setEditContext(null);
  }, [editing, editContextMatches]);

  const applySwitch = (next: { engagementId?: string; month?: string }) => {
    if (next.engagementId !== undefined) setEngagementId(next.engagementId);
    if (next.month !== undefined) setMonth(next.month);
    // Der Hinweis zum Mail-Entwurf gehoert zum vorigen Bericht — er darf nicht
    // ueber den Wechsel hinaus stehen bleiben.
    setMailPrepared(false);
  };

  /**
   * Wechsel von Beauftragung oder Monat — mit Rueckfrage, falls im Editor
   * ungespeicherte Aenderungen liegen.
   *
   * Stillschweigend verwerfen waere kein Fortschritt: Eine von Hand
   * formulierte Zusammenfassung ist Arbeit, die niemand unbemerkt verlieren
   * darf. Weiterarbeiten mit vermischten Daten ist aber ausgeschlossen —
   * die Rueckfrage laesst nur „hierbleiben" oder „verwerfen und wechseln" zu.
   */
  const requestSwitch = (next: { engagementId?: string; month?: string }) => {
    if (isEditing && editorRef.current?.isDirty()) {
      setPendingSwitch(next);
      return;
    }
    if (editing) exitEdit();
    applySwitch(next);
  };

  /**
   * „Speichern": nur den INHALT des Editors sichern.
   *
   * Zuvor ging das umschliessende Element mit — pro Zyklus eine zusaetzliche
   * Verschachtelung, und nach ein paar Runden ein Markup, das niemand mehr
   * lesen konnte.
   */
  const handleSaveEdit = () => {
    const html = editorRef.current?.getHtml();
    if (!html) return;
    saveEdited.mutate(html, {
      onSuccess: () => {
        setEditing(false);
        setEditSnapshot(null);
      },
    });
  };

  /**
   * Kopiert den Bericht als `text/html` — in Outlook eingefügt entsteht damit
   * exakt die Darstellung aus `ReportView`. Der Klartext geht als Fallback mit
   * (Editoren ohne HTML-Unterstützung). Kann der Browser kein `ClipboardItem`,
   * bleibt der Klartext übrig, statt dass gar nichts passiert.
   */
  const copyReportToClipboard = async () => {
    if (!report) return;
    const text = reportPlainText;
    // Fuer Outlook aufbereiten: Farben zusaetzlich als `<font color>`, sonst
    // wirft Word beim Einfuegen jedes `style="color:…"` weg (s. outlookHtml.ts).
    const roh = reportRef.current?.innerHTML;
    const html = roh ? toOutlookHtml(roh) : undefined;
    try {
      if (html && typeof ClipboardItem !== "undefined") {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([text], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(text);
      }
    } catch {
      await navigator.clipboard.writeText(text);
    }
  };

  /**
   * „Bericht kopieren".
   *
   * Die Rueckmeldung war sehr fluechtig: Der Knopftext wechselte kurz auf
   * „Kopiert" und wieder zurueck — in zwei aufeinanderfolgenden Bildschirmfotos
   * war davon nichts mehr zu sehen (Befund U9). Fuer den entscheidenden
   * Handgriff der ganzen Anwendung ist das zu wenig. Jetzt zusaetzlich eine
   * Einblendung, die stehen bleibt, und der Knopf haelt seinen Zustand laenger.
   */
  const handleCopy = async () => {
    await copyReportToClipboard();
    setCopied(true);
    notifications.show({
      message: t("pages.statusReports.copiedToast"),
      color: "green",
      autoClose: 4000,
      withBorder: true,
    });
    setTimeout(() => setCopied(false), 4000);
  };

  /**
   * „Mail entwerfen" — beide Schritte auf einen Klick (Befund U3).
   *
   * Der Bericht reist NICHT in der `mailto:`-URL mit. Er stand dort als
   * Klartext, damit der Entwurf auch ohne Einfuegen vollstaendig ist —
   * diese Eigenschaft haelt der Wirklichkeit aber nicht stand: Windows
   * kappt lange `mailto:`-Ziele, und beim Empfaenger kam abgeschnittener
   * Text an (im Betrieb beobachtet). Ausserdem traegt `mailto:` nur
   * Klartext, die gestaltete Fassung waere ohnehin verloren.
   *
   * Deshalb wie in der abgeloesten App: Derselbe Klick legt die
   * formatierte Fassung in die Zwischenablage und oeffnet den Entwurf mit
   * Anschreiben; eingefuegt wird mit Strg+V. Der Hinweis daneben sagt es.
   */
  const handleDraftMail = () => {
    void copyReportToClipboard();
    setMailPrepared(true);
  };

  if (loadingEngagements) {
    return <Skeleton height={320} />;
  }

  // Vor der Leermenge pruefen: Ein fehlgeschlagener Abruf sah zuvor aus wie
  // „keine Beauftragung sichtbar" (Befund W7).
  if (engagementsFailed) {
    return (
      <Stack gap="md">
        <Seitenkopf />
        <Alert color="red" variant="light" title={t("common.error")}>
          {(engagementsError as Error | null)?.message ??
            t("common.errorLoading")}
        </Alert>
      </Stack>
    );
  }

  if (!engagements || engagements.length === 0) {
    return (
      <Stack gap="md">
        <Seitenkopf />
        <Paper p="xl" withBorder>
          <Stack align="center" gap="sm">
            <Text fw={600}>{t("pages.statusReports.emptyTitle")}</Text>
            <Text c="dimmed" size="sm" ta="center" maw={520}>
              {t("pages.statusReports.emptyBody")}
            </Text>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  // Auswahl nach Service Lead gruppiert (wie die Sidebar des Originals).
  // Standard: nur Beauftragungen mit gebuchten Stunden im Vormonat.
  const visible = showAll
    ? engagements
    : engagements.filter((e) => e.activeLastMonth || e.own);
  const toOption = (e: (typeof visible)[number]) => ({
    value: e.engagementId,
    label: `${e.customerName} — ${e.segmentName}${e.own ? " ★" : ""}`,
  });

  /**
   * Die eigenen Beauftragungen als ERSTE Gruppe (Befund U6).
   *
   * Zuvor war die Liste rein alphabetisch nach Service Lead gruppiert und
   * begann bei „Aleksandar Zaki"; die eigenen Beauftragungen standen mitten
   * darin unter dem eigenen Namen. Wer nicht sucht, sondern blaettert,
   * blaettert lange — und das Erste, was jemand braucht, ist das Eigene.
   * Bewusst aus den Lead-Gruppen HERAUSgenommen und nicht zusaetzlich
   * angezeigt: Ein Eintrag zweimal in derselben Liste ist keine Hilfe.
   */
  const own = visible.filter((e) => e.own);
  const byLead = new Map<string, typeof visible>();
  for (const e of visible) {
    if (e.own) continue;
    const key = e.leadName ?? t("pages.statusReports.withoutLead");
    byLead.set(key, [...(byLead.get(key) ?? []), e]);
  }
  const engagementOptions = [
    ...(own.length > 0
      ? [
          {
            group: t("pages.statusReports.ownEngagements"),
            items: own.map(toOption),
          },
        ]
      : []),
    ...[...byLead.entries()]
      .sort(([a], [b]) => a.localeCompare(b, "de"))
      .map(([lead, items]) => ({
        group: lead,
        items: items.map(toOption),
      })),
  ];

  return (
    <Stack gap="md">
      <Seitenkopf />

      <Group align="flex-end" gap="md">
        <Select
          label={t("pages.statusReports.engagement")}
          data={engagementOptions}
          value={engagementId}
          onChange={(v) => {
            if (v === null || v === engagementId) return;
            requestSwitch({ engagementId: v });
          }}
          searchable
          w={420}
        />
        <Select
          label={t("pages.statusReports.month")}
          data={months.map((m) => ({
            value: m,
            label: formatMonth(m, locale),
          }))}
          value={month}
          onChange={(v) => {
            const next = v ?? months[0]!;
            if (next === month) return;
            requestSwitch({ month: next });
          }}
          w={220}
        />
        <Checkbox
          label={t("pages.statusReports.showAllCustomers")}
          description={t("pages.statusReports.showAllCustomersHint")}
          checked={showAll}
          onChange={(e) => setShowAll(e.currentTarget.checked)}
          pb={6}
        />
      </Group>

      {/* Rueckfrage vor dem Wechsel, wenn im Editor ungespeicherte Aenderungen
          liegen (Befund B1). Es gibt bewusst nur zwei Ausgaenge — mit
          vermischten Daten weiterarbeiten ist keiner davon. */}
      <Modal
        opened={pendingSwitch !== null}
        onClose={() => setPendingSwitch(null)}
        title={t("pages.statusReports.switchGuardTitle")}
        centered
      >
        <Stack gap="md">
          <Text size="sm">{t("pages.statusReports.switchGuardBody")}</Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setPendingSwitch(null)}>
              {t("pages.statusReports.switchGuardStay")}
            </Button>
            <Button
              color="red"
              onClick={() => {
                const next = pendingSwitch;
                setPendingSwitch(null);
                exitEdit();
                if (next) applySwitch(next);
              }}
            >
              {t("pages.statusReports.switchGuardDiscard")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {loadingReport && <Skeleton height={420} />}

      {/* Zuvor rendete der Zweig bei Fehler gar nichts — eine leere Flaeche
          statt einer Meldung (Befund W7). */}
      {reportFailed && !loadingReport && (
        <Alert color="red" variant="light" title={t("common.error")}>
          {(reportError as Error | null)?.message ?? t("common.errorLoading")}
        </Alert>
      )}

      {report && !loadingReport && (
        <Stack gap="md">
          <Paper p="md" withBorder>
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Text fw={600}>{report.engagementName}</Text>
                <Text c="dimmed" size="sm">
                  {formatMonth(report.reportMonth, locale)}
                </Text>
              </Stack>
              <Group gap="sm">
                <Badge color={report.status === "sent" ? "green" : "gray"}>
                  {t(`pages.statusReports.status.${report.status}`)}
                </Badge>
                <Button
                  size="xs"
                  variant="default"
                  loading={aiSummaries.isPending}
                  onClick={() => aiSummaries.mutate()}
                  title={t("pages.statusReports.aiSummariesHint")}
                >
                  {t("pages.statusReports.aiSummaries")}
                </Button>
                <Button size="xs" variant="default" onClick={handleCopy}>
                  {copied
                    ? t("pages.statusReports.copied")
                    : t("pages.statusReports.copy")}
                </Button>
                {/*
                  EIN Weg zum Entwurf, und zwar ueber Outlook im Web.

                  Vorher standen hier zwei Knoepfe. Die Messung an zwei echten
                  Mails (2026-09-04) hat den Unterschied entschieden: Outlook
                  Desktop plaettet beim Einfuegen die Typografie — von allen
                  Groessenabstufungen bleiben zwei uebrig, die Schriftart geht
                  groesstenteils verloren (12 `font-family` gegenueber 102 im
                  Web, 20 `font-size` gegenueber 111). Die Farben ueberleben
                  beide Wege, die Gliederung nur einen. Zwei Knoepfe hiessen:
                  Die Haelfte der Berichte geht in der schlechteren Fassung
                  raus, ohne dass jemand weiss warum.

                  Ohne Empfaenger ein ECHTER `<button disabled>`, mit
                  Empfaenger ein Link (Befund U-neu).

                  Zuvor stand hier ein einziger Knopf mit `component="a"` und
                  `disabled`. Der wurde damit zu `<a disabled href="mailto:…">`
                  — und `disabled` ist an einem Anker schlicht kein HTML-
                  Attribut. Mantine faerbte ihn ueber `data-disabled` grau,
                  `pointer-events` blieb aber `auto`: Der Knopf sah gesperrt
                  aus, feuerte beim Klick trotzdem `handleDraftMail` und
                  oeffnete `mailto:` OHNE Empfaenger. Nachgewiesen im e2e-Fall
                  „ausgegrautes ‚Mail entwerfen' laesst sich nicht ausloesen".

                  Deshalb die Verzweigung statt eines `disabled`-Schalters: Ein
                  `<button disabled>` sperrt der Browser selbst, ein Anker nie.
                  Der Grund steht weiterhin am Knopf und darunter (Befund U2) —
                  ein ausgegrauter Knopf ohne Begruendung ist eine Sackgasse.
                */}
                {report.config.recipients.length === 0 ? (
                  <Button
                    size="xs"
                    variant="default"
                    disabled
                    title={t("pages.statusReports.draftMailDisabledHint")}
                  >
                    {t("pages.statusReports.draftMail")}
                  </Button>
) : (
                  <Button
                    size="xs"
                    variant="default"
                    component="a"
                    href={buildOutlookWebHref(
                      report,
                      REPORT_LOCALE,
                      berichtT,
                      angemeldeterName,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDraftMail}
                    title={t("pages.statusReports.draftMailWebHint")}
                  >
                    {t("pages.statusReports.draftMail")}
                  </Button>
                )}
                {isEditing ? (
                  <>
                    <Button
                      size="xs"
                      variant="filled"
                      disabled={saveEdited.isPending}
                      onClick={handleSaveEdit}
                    >
                      {t("pages.statusReports.save")}
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={handleCancelEdit}
                    >
                      {t("pages.statusReports.cancel")}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="xs"
                    variant="default"
                    onClick={handleStartEdit}
                  >
                    {t("pages.statusReports.edit")}
                  </Button>
                )}
                {report.editedHtml && !isEditing && (
                  <Button
                    size="xs"
                    variant="subtle"
                    disabled={resetEdited.isPending}
                    /* Ein Klick verwarf eine muehsam formulierte Fassung ohne
                       Rueckfrage (Befund U8). */
                    onClick={() => setConfirmRegenerate(true)}
                  >
                    {t("pages.statusReports.regenerate")}
                  </Button>
                )}
                {/* Hin- und Rueckweg (Befund B8): Zuvor gab es nur den Hinweg,
                    und nach einem Fehlklick war der Knopf ausgegraut — der
                    Status liess sich ueber die Oberflaeche nicht mehr
                    zuruecknehmen. */}
                {report.status === "sent" ? (
                  <Button
                    size="xs"
                    variant="light"
                    color="gray"
                    disabled={revertSent.isPending}
                    onClick={() => revertSent.mutate()}
                  >
                    {t("pages.statusReports.revertSent")}
                  </Button>
                ) : (
                  <Button
                    size="xs"
                    variant="light"
                    disabled={markSent.isPending}
                    onClick={() => setConfirmSend(true)}
                  >
                    {t("pages.statusReports.markSent")}
                  </Button>
                )}
              </Group>
            </Group>

            {/* Fehler aus den Aktionen dieser Zeile — HIER und nicht am
                Seitenkopf (Befund B7): Das Banner stand 1469 Pixel ueber dem
                Knopf, an dem der Anwender gerade stand. */}
            <MutationAlert
              mutations={[markSent, revertSent, saveEdited, resetEdited, aiSummaries]}
            />

            {report.config.recipients.length === 0 && (
              <Text c="dimmed" size="xs" mt="xs">
                {t("pages.statusReports.draftMailDisabledHint")}
              </Text>
            )}
          </Paper>

          {/* Der zweite Schritt zum Mail-Entwurf, benannt statt vorausgesetzt
              (Befund U3). */}
          {mailPrepared && (
            <Alert
              color="blue"
              variant="light"
              withCloseButton
              closeButtonLabel={t("common.close")}
              onClose={() => setMailPrepared(false)}
              data-testid="mail-draft-hint"
            >
              {t("pages.statusReports.draftMailHint")}
            </Alert>
          )}

          {/*
            Rueckfrage vor „Als versendet markieren" (Befund B8).

            Bewusst OHNE Empfaenger-Zwang (Befund U-neu). Der Nachtest schlug
            vor, den Knopf ohne hinterlegten Empfaenger auszugrauen. Fachlich
            waere das falsch: Der Vermerk behauptet nicht, das Werkzeug habe
            etwas verschickt — das CRM verschickt nichts, es haelt fest, dass
            DER BEARBEITER den Bericht verschickt hat. Wer ihn ueber sein
            Postfach, ein Kundenportal oder im Termin uebergeben hat, hat den
            Empfaenger nie im CRM gebraucht; ihn dafuer nachzupflegen waere
            eine erfundene Pflichtangabe, und die Buchhaltung des eigenen
            Arbeitsstands haenge dann an einem Feld, das nur der Mail-Entwurf
            braucht. Auch die Schnittstelle kennt diese Bedingung nicht
            (`markReportSent` prueft nur die Berechtigung). Ein Gate in der
            Oberflaeche waere also allein dort erfunden.

            Was der Nachtest zu Recht verlangt, ist Rueckmeldung — und die
            steht jetzt hier: Wer ohne Empfaenger vermerkt, erfaehrt, dass das
            gewollt geht und was der Vermerk bedeutet.
          */}
          <Modal
            opened={confirmSend}
            onClose={() => setConfirmSend(false)}
            title={t("pages.statusReports.markSentConfirmTitle")}
            centered
          >
            <Stack gap="md">
              <Text size="sm">
                {t("pages.statusReports.markSentConfirmBody")}
              </Text>
              {report.config.recipients.length === 0 && (
                <Alert
                  color="yellow"
                  variant="light"
                  data-testid="mark-sent-no-recipient"
                >
                  {t("pages.statusReports.markSentNoRecipientHint")}
                </Alert>
              )}
              <Group justify="flex-end" gap="sm">
                <Button variant="default" onClick={() => setConfirmSend(false)}>
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={() => {
                    setConfirmSend(false);
                    markSent.mutate();
                  }}
                >
                  {t("pages.statusReports.markSentConfirm")}
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* Rueckfrage vor „Neu erzeugen" (Befund U8). */}
          <Modal
            opened={confirmRegenerate}
            onClose={() => setConfirmRegenerate(false)}
            title={t("pages.statusReports.regenerateConfirmTitle")}
            centered
          >
            <Stack gap="md">
              <Text size="sm">
                {t("pages.statusReports.regenerateConfirmBody")}
              </Text>
              <Group justify="flex-end" gap="sm">
                <Button
                  variant="default"
                  onClick={() => setConfirmRegenerate(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  color="red"
                  onClick={() => {
                    setConfirmRegenerate(false);
                    resetEdited.mutate();
                  }}
                >
                  {t("pages.statusReports.regenerateConfirm")}
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* Hinweise richten sich an den Bearbeiter, nicht an den Kunden —
              sie folgen der Oberflächensprache. Das Backend liefert seit W8
              einen Code statt eines fertigen deutschen Satzes. */}
          {report.notices.map((notice) => (
            <Alert key={notice} color="yellow" variant="light">
              {t(`pages.statusReports.notice.${notice}`)}
            </Alert>
          ))}

          {/* Der Bericht selbst — originalgetreu, ohne Bedienelemente darin.
              `ref` liefert das Markup für „Bericht kopieren" (text/html). */}
          {aiSummaries.data && aiSummaries.data.failures.length > 0 && (
            <Alert color="yellow" variant="light">
              {t("pages.statusReports.aiPartial", {
                generated: aiSummaries.data.generated,
                failed: aiSummaries.data.failures.length,
              })}
            </Alert>
          )}
          {report.editedHtml && (
            <Alert color="blue" variant="light">
              {t("pages.statusReports.editedNotice")}
            </Alert>
          )}
          <Paper withBorder p={0} style={{ overflowX: "auto" }}>
            {isEditing ? (
              /* Eigener, von React NICHT verwalteter Knoten — ein Neuaufbau
                 der Seite darf die Handeingaben nicht verwerfen (G10). */
              <EditableHtml ref={editorRef} initialHtml={editSnapshot ?? ""} />
            ) : sanitizedEditedHtml !== null ? (
              /* Gespeichertes Markup wird vor der Anzeige bereinigt — es wird
                 auch anderen Nutzern gezeigt, also nie ungeprüft einsetzen.
                 Das `ref` sitzt auf demselben Knoten wie der Inhalt, damit
                 „kopieren" und „bearbeiten" keinen Rahmen mitnehmen. */
              <div
                ref={reportRef}
                data-testid="report-body"
                dangerouslySetInnerHTML={{ __html: sanitizedEditedHtml }}
              />
            ) : (
              <div ref={reportRef} data-testid="report-body">
                <ReportView report={report} />
              </div>
            )}
          </Paper>

          <Title order={4} mt="lg">
            {t("pages.statusReports.editSection")}
          </Title>

          {/* `key` aus der Beauftragungs-Id (Befund W5): SettingsCard liest
              ihren Zustand nur beim Einhaengen. Ohne Neuaufbau beim Wechsel
              behielte sie Empfaenger und Anrede der VORIGEN Beauftragung — und
              schriebe sie beim Speichern auf die neue. Der naechste
              Mail-Entwurf ginge an den falschen Kunden. */}
          <SettingsCard
            key={report.engagementId}
            config={report.config}
            busy={updateConfig.isPending}
            saveState={updateConfig}
            onSave={(patch) => updateConfig.mutate(patch)}
          />

          {/* Abwesenheiten der Ansprechpartner — bewusst hier und nicht nur
              unter Einstellungen: Dass eine Workshop-Woche im Bericht fehlt,
              merkt man beim Lesen des Berichts. */}
          <AbsenceContactPanel contacts={report.contacts} />

          <Card withBorder padding="md">
            <Title order={4} mb="sm">
              {t("pages.statusReports.budgetSelection")}
            </Title>
            <Text c="dimmed" size="sm" mb="sm">
              {t("pages.statusReports.budgetSelectionHint")}
            </Text>
            {report.availableBudgets.length === 0 ? (
              <Text c="dimmed" size="sm">
                {t("pages.statusReports.noBudgetPool")}
              </Text>
            ) : (
              <Stack gap="xs">
                {report.availableBudgets.map((b) => {
                  const selectedIds =
                    report.config.selectedProjectIds ??
                    report.budgets.map((x) => x.projectId);
                  const checked = selectedIds.includes(b.projectId);
                  return (
                    <Group key={b.projectId} align="flex-start" wrap="nowrap" gap="sm">
                    <Checkbox
                      style={{ flex: 1 }}
                      checked={checked}
                      disabled={updateConfig.isPending}
                      /* Warum die Automatik dieses Budget NICHT naehme
                         (Befund U1): Bei August Storck stand genau ein Budget
                         im Bericht — ausgerechnet das mit dem kleinsten Rest —
                         und der Erklaertext daneben nannte nur „die zwei mit
                         dem groessten Rest". Welche Stufe der Regel ein Budget
                         reisst, war von aussen nicht ablesbar. */
                      description={
                        b.autoReason
                          ? t(`pages.statusReports.autoReason.${b.autoReason}`)
                          : undefined
                      }
                      label={
                        <Group gap="xs">
                          <Text size="sm">{b.name}</Text>
                          {/* Ein Umsetzungsprojekt sieht in dieser Liste aus
                              wie ein Servicekontingent, ist aber keines — es
                              kommt aus einem anderen Bereich und steht hier
                              nur, weil es zum selben Kunden gehoert. Ohne das
                              Kennzeichen haekelt irgendwann jemand das falsche
                              Budget in einen Kundenbericht. */}
                          {b.source === "pu" ? (
                            <Badge size="xs" variant="light" color="grape">
                              {t("pages.statusReports.puBadge")}
                            </Badge>
                          ) : null}
                          <Badge size="xs" variant="light">
                            {b.status}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {t("pages.statusReports.left")}: {formatEur(b.leftEur, locale)}
                          </Text>
                        </Group>
                      }
                      onChange={(event) => {
                        const next = event.currentTarget.checked
                          ? [...selectedIds, b.projectId]
                          : selectedIds.filter((id) => id !== b.projectId);
                        updateConfig.mutate({ selectedProjectIds: next });
                      }}
                    />
                    {/* Die Bestellnummer gehoert ans Budget, nicht an den
                        Monat: Sie steht im Vertrag. Gespeichert wird beim
                        Verlassen des Feldes. */}
                    <OrderNumberInput
                      projectId={b.projectId}
                      value={b.orderNumber}
                      busy={setOrderNumber.isPending}
                      onSave={(projectId, orderNumber) =>
                        setOrderNumber.mutate({ projectId, orderNumber })
                      }
                    />
                    </Group>
                  );
                })}
                {report.config.selectedProjectIds !== null && (
                  <Button
                    size="xs"
                    variant="subtle"
                    w="fit-content"
                    disabled={updateConfig.isPending}
                    onClick={() =>
                      updateConfig.mutate({ selectedProjectIds: null })
                    }
                  >
                    {t("pages.statusReports.resetToAuto")}
                  </Button>
                )}
                <MutationAlert mutations={[updateConfig]} />
              </Stack>
            )}
          </Card>

          <HighlightsCard
            highlights={report.highlights}
            onAdd={(body) => addHighlight.mutate({ body })}
            onRemove={(id) => removeHighlight.mutate(id)}
            busy={addHighlight.isPending || removeHighlight.isPending}
            mutations={[addHighlight, removeHighlight]}
          />

          {/* Statt eines Knopfes: WANN der Stand ist. Ein Knopf konnte nur
              bekannte Vorgaenge nachziehen, nicht neue holen — und beides
              braucht man im selben Fall. Eine ehrliche Zeitangabe ist mehr
              wert als eine halbe Aktualisierung. */}
          {report.ticketsSyncedAt && (
            <Text size="xs" c="dimmed">
              {t("pages.statusReports.ticketsSyncedAt", {
                zeitpunkt: new Date(report.ticketsSyncedAt).toLocaleString(
                  locale,
                  { dateStyle: "short", timeStyle: "short" },
                ),
              })}
            </Text>
          )}
          <TicketCard
            title={t("pages.statusReports.openTickets")}
            tickets={report.openTickets}
            emptyLabel={t("pages.statusReports.noTickets")}
            onAdd={(row) => addOverride.mutate({ ...row, section: "open" })}
            onRemove={(id) => removeOverride.mutate(id)}
            onSetNote={(jiraKey, nextSteps) =>
              setTicketNote.mutate({ jiraKey, nextSteps })
            }
            onSetHidden={(jiraKey, hidden) =>
              setTicketHidden.mutate({ jiraKey, hidden })
            }
            busy={
              addOverride.isPending ||
              removeOverride.isPending ||
              setTicketNote.isPending ||
              setTicketHidden.isPending
            }
            mutations={[addOverride, removeOverride, setTicketNote, setTicketHidden]}
          />
          <TicketCard
            title={t("pages.statusReports.doneTickets")}
            tickets={report.doneTickets}
            emptyLabel={t("pages.statusReports.noTickets")}
            allowHighlight
            onAdd={(row) => addOverride.mutate({ ...row, section: "done" })}
            onRemove={(id) => removeOverride.mutate(id)}
            onSetNote={(jiraKey, nextSteps) =>
              setTicketNote.mutate({ jiraKey, nextSteps })
            }
            onSetHidden={(jiraKey, hidden) =>
              setTicketHidden.mutate({ jiraKey, hidden })
            }
            busy={
              addOverride.isPending ||
              removeOverride.isPending ||
              setTicketNote.isPending ||
              setTicketHidden.isPending
            }
            mutations={[addOverride, removeOverride, setTicketNote, setTicketHidden]}
          />
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Einstellungen je Beauftragung — Anrede, Empfänger, Ticketstatistik.
 * Entspricht dem Dialog „⚙ Einstellungen" der Altanwendung.
 */
function SettingsCard({
  config,
  busy,
  saveState,
  onSave,
}: {
  config: StatusReportConfig;
  busy: boolean;
  saveState: ResettableMutation;
  onSave: (patch: StatusReportConfigInput) => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"sie" | "du">(config.salutationMode);
  const [name, setName] = useState(config.salutationName ?? "");
  /** Frei gepflegte Anrede — sticht Modus und Vornamen (Befund U4). */
  const [salutation, setSalutation] = useState(config.salutation ?? "");
  const [recipients, setRecipients] = useState(config.recipients.join(", "));
  const [cc, setCc] = useState(config.ccRecipients.join(", "));

  const split = (v: string) =>
    v
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);

  return (
    <Card withBorder padding="md">
      <Title order={4} mb="sm">
        {t("pages.statusReports.settings")}
      </Title>
      <Stack gap="sm">
        <Group align="flex-end" gap="md">
          <Select
            label={t("pages.statusReports.salutationMode")}
            data={[
              { value: "sie", label: t("pages.statusReports.salutationSie") },
              { value: "du", label: t("pages.statusReports.salutationDu") },
            ]}
            value={mode}
            onChange={(v) => setMode(v === "du" ? "du" : "sie")}
            w={220}
          />
          <TextInput
            label={t("pages.statusReports.salutationName")}
            placeholder={t("pages.statusReports.salutationNamePlaceholder")}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            disabled={mode !== "du"}
            w={360}
          />
        </Group>
        {/*
          Der Hinweis steht UNTER der Gruppe, nicht als `description` am Feld:
          Dort schiebt er das Eingabefeld nach unten, waehrend das Nachbarfeld
          oben bleibt — die beiden standen dann sichtbar versetzt (07.09.2026).
        */}
        <Text size="xs" c="dimmed" mt={-8}>
          {t("pages.statusReports.salutationNameHint")}
        </Text>
        {/*
          „Sehr geehrte Damen und Herren" an einen festen Ansprechpartner, den
          das Werkzeug namentlich kennt — unpassend fuer ein monatliches
          Schreiben (Befund U4). Das Datenmodell trug die freie Anrede laengst
          („sticht beide", s. `buildMailBody`), nur die Oberflaeche zeigte sie
          nicht. Bewusst Freitext statt Auswahl aus Frau/Herr: Damit gehen auch
          Titel („Sehr geehrter Herr Prof. Dr. Müller") und Faelle, die kein
          Geschlechtsschalter trifft — und niemand muss aus einem Nachnamen
          raten.
        */}
        <TextInput
          label={t("pages.statusReports.salutationCustom")}
          description={t("pages.statusReports.salutationCustomHint")}
          placeholder={t("pages.statusReports.salutationCustomPlaceholder")}
          value={salutation}
          onChange={(e) => setSalutation(e.currentTarget.value)}
        />
        <TextInput
          label={t("pages.statusReports.recipients")}
          placeholder="ansprechpartner@kunde.de"
          value={recipients}
          onChange={(e) => setRecipients(e.currentTarget.value)}
        />
        <TextInput
          label={t("pages.statusReports.ccRecipients")}
          value={cc}
          onChange={(e) => setCc(e.currentTarget.value)}
        />
        <Checkbox
          label={t("pages.statusReports.showTicketStats")}
          description={t("pages.statusReports.showTicketStatsHint")}
          checked={config.showTicketStats}
          disabled={busy}
          onChange={(e) =>
            onSave({ showTicketStats: e.currentTarget.checked })
          }
        />
        {/* Die Meldung steht beim Knopf, nicht am Seitenkopf (Befund B7). */}
        <MutationAlert mutations={[saveState]} />
        <Button
          size="sm"
          w="fit-content"
          disabled={busy}
          onClick={() =>
            onSave({
              salutationMode: mode,
              salutationName: name.trim() || null,
              salutation: salutation.trim() || null,
              recipients: split(recipients),
              ccRecipients: split(cc),
            })
          }
        >
          {t("pages.statusReports.save")}
        </Button>
      </Stack>
    </Card>
  );
}

/** Redaktionelle Hervorhebungen — freier Text, anlegen und entfernen. */
function HighlightsCard({
  highlights,
  onAdd,
  onRemove,
  busy,
  mutations,
}: {
  highlights: StatusReportHighlight[];
  onAdd: (body: string) => void;
  onRemove: (id: string) => void;
  busy: boolean;
  mutations: ResettableMutation[];
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");

  const submit = () => {
    const body = draft.trim();
    if (!body) return;
    onAdd(body);
    setDraft("");
  };

  return (
    <Card withBorder padding="md">
      <Title order={4} mb="sm">
        {t("pages.statusReports.highlights")}
      </Title>
      <Stack gap="xs">
        {highlights.length === 0 && (
          <Text c="dimmed" size="sm">
            {t("pages.statusReports.noHighlights")}
          </Text>
        )}
        {highlights.map((h) => (
          <Group key={h.id} justify="space-between" wrap="nowrap">
            <Text size="sm">{h.body}</Text>
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label={t("pages.statusReports.remove")}
              disabled={busy}
              onClick={() => onRemove(h.id)}
            >
              ×
            </ActionIcon>
          </Group>
        ))}
        <Group gap="xs" align="flex-end">
          <TextInput
            flex={1}
            placeholder={t("pages.statusReports.highlightPlaceholder")}
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
          />
          <Button size="sm" disabled={busy || !draft.trim()} onClick={submit}>
            {t("pages.statusReports.add")}
          </Button>
        </Group>
        <MutationAlert mutations={mutations} />
      </Stack>
    </Card>
  );
}

/**
 * Freitextfeld für „Status und nächste Schritte" an einem Vorgang.
 *
 * **Gespeichert wird beim Verlassen des Feldes, nicht bei jedem Tastendruck.**
 * Der Text ist mehrere Sätze lang; ein Aufruf je Zeichen wäre eine Last ohne
 * Gegenwert, und ein halb getippter Satz gehört nicht in den Bericht.
 *
 * **Nur für Vorgänge mit Nummer.** Die Notiz hängt an der Vorgangsnummer, weil
 * sie sonst Spiegel-Vorgänge nicht erreichen könnte, ohne sie zu verdrängen.
 * Handzeilen ohne Nummer sind ohnehin frei getippt — dort steht der nächste
 * Schritt einfach im Text.
 */
/**
 * Bestellnummer eines Budgets.
 *
 * **Gespeichert beim Verlassen des Feldes**, wie bei den naechsten Schritten:
 * Ein Aufruf je Tastendruck waere eine Last ohne Gegenwert. Leerer Text
 * entfernt die Nummer.
 */
function OrderNumberInput({
  projectId,
  value,
  busy,
  onSave,
}: {
  projectId: string;
  value: string | null;
  busy: boolean;
  onSave: (projectId: string, orderNumber: string) => void;
}) {
  const { t } = useTranslation();
  const [entwurf, setEntwurf] = useState(value ?? "");

  // Kommt der Bericht neu vom Server, gilt dessen Stand.
  useEffect(() => {
    setEntwurf(value ?? "");
  }, [value]);

  return (
    <TextInput
      size="xs"
      w={200}
      disabled={busy}
      placeholder={t("pages.statusReports.orderNumberPlaceholder")}
      aria-label={t("pages.statusReports.orderNumberLabel")}
      value={entwurf}
      onChange={(e) => setEntwurf(e.currentTarget.value)}
      onBlur={() => {
        if (entwurf.trim() === (value ?? "").trim()) return;
        onSave(projectId, entwurf);
      }}
    />
  );
}

function NextStepsInput({
  jiraKey,
  value,
  busy,
  onSave,
}: {
  jiraKey: string | null;
  value: string | null;
  busy: boolean;
  onSave: (jiraKey: string, nextSteps: string) => void;
}) {
  const { t } = useTranslation();
  const [entwurf, setEntwurf] = useState(value ?? "");

  // Kommt der Bericht neu vom Server, gilt dessen Stand — sonst überschriebe
  // ein alter Entwurf im Feld eine Änderung, die jemand anders gespeichert hat.
  useEffect(() => {
    setEntwurf(value ?? "");
  }, [value]);

  if (!jiraKey) {
    return (
      <Text size="xs" c="dimmed">
        {t("pages.statusReports.nextStepsNeedsKey")}
      </Text>
    );
  }

  return (
    <Textarea
      autosize
      minRows={1}
      maxRows={6}
      size="xs"
      disabled={busy}
      placeholder={t("pages.statusReports.nextStepsPlaceholder")}
      aria-label={t("pages.statusReports.report.colNextSteps")}
      value={entwurf}
      onChange={(e) => setEntwurf(e.currentTarget.value)}
      onBlur={() => {
        if (entwurf.trim() === (value ?? "").trim()) return;
        onSave(jiraKey, entwurf);
      }}
    />
  );
}

function TicketCard({
  title,
  tickets,
  emptyLabel,
  onAdd,
  onRemove,
  onSetNote,
  onSetHidden,
  busy,
  mutations,
  allowHighlight = false,
}: {
  title: string;
  tickets: StatusReportTicket[];
  emptyLabel: string;
  onAdd: (row: {
    jiraKey: string | null;
    summary: string;
    ticketStatus: string | null;
    highlight?: boolean;
    valueDescription?: string | null;
  }) => void;
  onRemove: (id: string) => void;
  /**
   * Speichert „Status und nächste Schritte" für einen Vorgang. Leerer Text
   * entfernt die Notiz — und damit die Spalte, sobald keine mehr übrig ist.
   */
  onSetNote: (jiraKey: string, nextSteps: string) => void;
  /**
   * Blendet einen Vorgang im KUNDENBERICHT aus. In dieser Tabelle bleibt er
   * stehen, sonst gaebe es keinen Weg zurueck.
   */
  onSetHidden: (jiraKey: string, hidden: boolean) => void;
  busy: boolean;
  mutations: ResettableMutation[];
  /** Erledigte Tickets lassen sich als „Highlight des Monats" markieren. */
  allowHighlight?: boolean;
}) {
  const { t } = useTranslation();
  const [jiraKey, setJiraKey] = useState("");
  const [summary, setSummary] = useState("");
  const [ticketStatus, setTicketStatus] = useState("");
  const [highlight, setHighlight] = useState(false);
  const [valueDescription, setValueDescription] = useState("");

  const submit = () => {
    const trimmed = summary.trim();
    if (!trimmed) return;
    onAdd({
      jiraKey: jiraKey.trim() || null,
      summary: trimmed,
      ticketStatus: ticketStatus.trim() || null,
      ...(allowHighlight
        ? {
            highlight,
            valueDescription: valueDescription.trim() || null,
          }
        : {}),
    });
    setJiraKey("");
    setSummary("");
    setTicketStatus("");
    setHighlight(false);
    setValueDescription("");
  };

  return (
    <Card withBorder padding="md">
      <Title order={4} mb="sm">
        {title}
      </Title>
      {tickets.length === 0 ? (
        <Text c="dimmed" size="sm">
          {emptyLabel}
        </Text>
      ) : (
        <Table>
          <Table.Tbody>
            {tickets.map((ticket, idx) => (
              <Table.Tr key={ticket.overrideId ?? `${idx}-${ticket.summary}`}>
                <Table.Td w={110}>
                  <Text size="sm" c="dimmed">
                    {ticket.jiraKey ?? "–"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{ticket.summary}</Text>
                </Table.Td>
                <Table.Td w={160}>
                  <Text size="sm" c="dimmed">
                    {ticket.ticketStatus ?? "–"}
                  </Text>
                </Table.Td>
                <Table.Td w={280}>
                  <NextStepsInput
                    jiraKey={ticket.jiraKey}
                    value={ticket.nextSteps}
                    busy={busy}
                    onSave={onSetNote}
                  />
                </Table.Td>
                <Table.Td w={76}>
                  <Group gap={2} wrap="nowrap">
                    {/* Ausblenden gibt es nur mit Vorgangsnummer: Die
                        Entscheidung haengt an ihr, nicht an der Zeile.
                        Handzeilen ohne Nummer loescht man stattdessen. */}
                    {ticket.jiraKey && (
                      <ActionIcon
                        variant="subtle"
                        color={ticket.hidden ? "orange" : "gray"}
                        aria-label={t(
                          ticket.hidden
                            ? "pages.statusReports.unhideTicket"
                            : "pages.statusReports.hideTicket",
                        )}
                        title={t(
                          ticket.hidden
                            ? "pages.statusReports.unhideTicketHint"
                            : "pages.statusReports.hideTicketHint",
                        )}
                        disabled={busy}
                        onClick={() =>
                          onSetHidden(ticket.jiraKey!, !ticket.hidden)
                        }
                      >
                        {ticket.hidden ? "🚫" : "👁"}
                      </ActionIcon>
                    )}
                    {ticket.overrideId && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={t("pages.statusReports.remove")}
                        disabled={busy}
                        onClick={() => onRemove(ticket.overrideId!)}
                      >
                        ×
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Group gap="xs" align="flex-end" mt="sm">
        <TextInput
          w={110}
          placeholder={t("pages.statusReports.jiraKeyPlaceholder")}
          value={jiraKey}
          onChange={(e) => setJiraKey(e.currentTarget.value)}
        />
        <TextInput
          flex={1}
          placeholder={t("pages.statusReports.summaryPlaceholder")}
          value={summary}
          onChange={(e) => setSummary(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
        <TextInput
          w={160}
          placeholder={t("pages.statusReports.statusPlaceholder")}
          value={ticketStatus}
          onChange={(e) => setTicketStatus(e.currentTarget.value)}
        />
        <Button size="sm" disabled={busy || !summary.trim()} onClick={submit}>
          {t("pages.statusReports.add")}
        </Button>
      </Group>

      <MutationAlert mutations={mutations} />

      {allowHighlight && (
        <Group gap="xs" align="flex-end" mt="xs">
          <Checkbox
            label={t("pages.statusReports.markHighlight")}
            checked={highlight}
            onChange={(e) => setHighlight(e.currentTarget.checked)}
          />
          <TextInput
            flex={1}
            placeholder={t("pages.statusReports.valueDescriptionPlaceholder")}
            value={valueDescription}
            onChange={(e) => setValueDescription(e.currentTarget.value)}
            disabled={!highlight}
          />
        </Group>
      )}
    </Card>
  );
}
